import React from 'react';
import { Trash2, UserX, Edit, Pencil, Archive } from 'lucide-react';
import type { Resident } from '../types';
import type { User } from '../types/auth';
import EditPaymentModal from './EditPaymentModal';
import { formatDateDDMMYYYY } from '../utils/format';
import { getOpenCicilanSisa } from '../utils/cicilan';
import { NotificationModal } from './NotificationModal';
import { ConfirmationModal } from './ConfirmationModal';

interface ResidentInfoProps {
  position: 1 | 2;
  resident?: Resident;
  currentUser?: User;
  onDeletePaymentPeriod?: (paymentPeriodId: string) => Promise<boolean>;
  onDeleteResident?: (residentId: string) => Promise<boolean>;
  onEditResident?: (resident: Resident) => void;
  onBackupResident?: (residentId: string, reason?: string) => Promise<void>;
}

export function ResidentInfo({ position, resident, currentUser, onDeletePaymentPeriod, onDeleteResident, onEditResident, onBackupResident }: ResidentInfoProps) {
  const [editingPeriodId, setEditingPeriodId] = React.useState<string | null>(null);
  const [isBackingUp, setIsBackingUp] = React.useState(false);
  const [notification, setNotification] = React.useState<{
    visible: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({ visible: false, type: 'success', title: '', message: '' });
  const [confirmation, setConfirmation] = React.useState<{
    visible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  
  // Check if user can edit - residents can only edit their own room's data
  const canEdit = currentUser && (
    currentUser.role !== 'resident' || // Non-residents can edit
    (currentUser.role === 'resident' && currentUser.roomNumber === resident?.roomNumber) // Residents can edit only their own room
  );
  const canBackup = !!(currentUser && ['yudha', 'irfan', 'oeoel'].includes(currentUser.username));
  
  const handleDeletePayment = async (paymentPeriodId: string, periodNumber: number) => {
    if (!onDeletePaymentPeriod) return;
    
    setConfirmation({
      visible: true,
      title: 'Hapus Bukti Pembayaran?',
      message: `Apakah Anda yakin ingin menghapus bukti pembayaran periode ${periodNumber}?\n\nData yang dihapus tidak dapat dikembalikan.`,
      onConfirm: async () => {
        const success = await onDeletePaymentPeriod(paymentPeriodId);
        if (success) {
          // Data will be refreshed automatically by the parent component
        } else {
          setNotification({
            visible: true,
            type: 'error',
            title: 'Gagal Menghapus',
            message: 'Gagal menghapus bukti pembayaran. Silakan coba lagi.'
          });
        }
      }
    });
  };

  const handleDeleteResident = async () => {
    if (!onDeleteResident || !resident) return;
    
    setConfirmation({
      visible: true,
      title: 'Hapus Penghuni?',
      message: `Apakah Anda yakin ingin menghapus penghuni "${resident.name}"?\n\nSemua data pembayaran penghuni ini juga akan terhapus.\nTindakan ini tidak dapat dibatalkan.`,
      onConfirm: async () => {
        const success = await onDeleteResident(resident.id);
        if (success) {
          // Data will be refreshed automatically by the parent component
        } else {
          setNotification({
            visible: true,
            type: 'error',
            title: 'Gagal Menghapus',
            message: 'Gagal menghapus penghuni. Silakan coba lagi.'
          });
        }
      }
    });
  };

  const handleEditResident = () => {
    if (onEditResident && resident) {
      onEditResident(resident);
    }
  };

  const handleBackupResident = async () => {
    if (!onBackupResident || !resident || isBackingUp || !canBackup) return;

    setIsBackingUp(true);
    try {
      await onBackupResident(resident.id, 'Manual backup');
      setNotification({
        visible: true,
        type: 'success',
        title: 'Backup Berhasil!',
        message: `Data penghuni "${resident.name}" beserta ${resident.paymentPeriods?.length || 0} periode pembayaran telah dibackup.`
      });
    } catch (error) {
      console.error('Backup error:', error);
      setNotification({
        visible: true,
        type: 'error',
        title: 'Backup Gagal',
        message: 'Gagal membackup penghuni. Silakan coba lagi.'
      });
    } finally {
      setIsBackingUp(false);
    }
  };

  if (!resident || !resident.name) {
    return (
      <div className="p-2 sm:p-5 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center font-medium truncate">Penghuni {position} (Kosong)</p>
      </div>
    );
  }

  return (
    <>
      <div className="p-2 sm:p-5 space-y-2 sm:space-y-4 bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm group-hover:shadow-md transition-shadow duration-200">
        <div>
          <div className="flex justify-between items-start mb-2 sm:mb-3 gap-1 sm:gap-2">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                {position}
              </div>
              <h3 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">Penghuni {position}</h3>
            </div>
            <div className="flex gap-0.5 sm:gap-1.5 flex-shrink-0">
              {onEditResident && canEdit && (
                <button
                  onClick={handleEditResident}
                  className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-1 sm:p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 touch-manipulation group/edit"
                  title={`Edit penghuni ${position}`}
                >
                  <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform duration-200 group-hover/edit:scale-110 group-hover/edit:rotate-12" />
                </button>
              )}
              {onBackupResident && canBackup && (
                <button
                  onClick={handleBackupResident}
                  disabled={isBackingUp}
                  className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 p-1 sm:p-2 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-all duration-200 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 group/backup"
                  title={`Backup data penghuni ${position}`}
                >
                  <Archive className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
              )}
              {onDeleteResident && canEdit && (
                <button
                  onClick={handleDeleteResident}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 sm:p-2 rounded transition-colors touch-manipulation"
                  title={`Hapus penghuni ${position}`}
                >
                  <UserX className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="space-y-0.5 sm:space-y-1 text-xs sm:text-sm">
            <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{resident.name}</p>
            <p className="text-gray-600 dark:text-gray-300 truncate">{resident.study}</p>
            <p className="text-gray-600 dark:text-gray-300 text-[10px] sm:text-sm truncate">{resident.phone}</p>
            {resident.motorcyclePlate && (
              <p className="text-gray-600 dark:text-gray-300 text-[10px] sm:text-sm truncate">
                <span className="font-medium">Plat:</span> {resident.motorcyclePlate}
              </p>
            )}
            <p className="text-gray-600 dark:text-gray-300 text-[10px] sm:text-sm">
              <span className="font-medium">Masuk:</span> {formatDateDDMMYYYY(resident.startDate)}
            </p>
            <p className="text-gray-600 dark:text-gray-300 text-[10px] sm:text-sm">
              <span className="font-medium">Pola:</span> {resident.paymentPattern} Bln
            </p>
            {(() => {
              const openSisa = getOpenCicilanSisa(resident);
              if (!openSisa) return null;
              return (
                <p className="text-blue-700 dark:text-blue-300 text-[10px] sm:text-sm font-medium">
                  Sisa cicilan jatuh tempo: {formatDateDDMMYYYY(openSisa.sisaDueDate)}
                  {openSisa.isOverdue ? ' (terlambat)' : ''}
                </p>
              );
            })()}
          </div>
        </div>

      {resident.paymentPeriods.map((period) => (
        <div key={period.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-2 sm:p-3 bg-white dark:bg-gray-700">
          <div className="flex justify-between items-start mb-1.5 sm:mb-2">
            <h4 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1 flex-wrap">
              <span className="truncate">Periode {period.periodNumber}</span>
              {period.category && (
                <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                  {period.category === 'Cicilan' && period.installmentNumber ? `${period.category} ${period.installmentNumber}` : period.category}
                </span>
              )}
            </h4>
            <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
              {canEdit && (
                <button
                  onClick={() => setEditingPeriodId(period.id)}
                  className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-1 rounded transition-colors touch-manipulation flex-shrink-0"
                  title={`Edit periode ${period.periodNumber}`}
                >
                  <Pencil className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                </button>
              )}
              {onDeletePaymentPeriod && canEdit && (
                <button
                  onClick={() => handleDeletePayment(period.id, period.periodNumber)}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded transition-colors touch-manipulation flex-shrink-0"
                  title={`Hapus bukti pembayaran periode ${period.periodNumber}`}
                >
                  <Trash2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="space-y-0.5 sm:space-y-1 text-[10px] sm:text-xs text-gray-600 dark:text-gray-300">
            <p className="truncate">
              <span className="font-medium">Bayar:</span> {formatDateDDMMYYYY(period.paymentProofDate)}
            </p>
            <p className="truncate">
              <span className="font-medium">Bukti:</span> {period.paymentProofLink ? (
                <a 
                  href={period.paymentProofLink} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline dark:text-blue-400 ml-1 break-all"
                >
                  Lihat
                </a>
              ) : ' -'}
            </p>
            <p className="truncate">
              <span className="font-medium">Berikutnya:</span> {formatDateDDMMYYYY(period.nextPaymentDate)}
            </p>
          </div>
          <EditPaymentModal
            isOpen={editingPeriodId === period.id}
            onClose={() => setEditingPeriodId(null)}
            resident={resident}
            period={period}
            onSave={async (res, per, updates) => {
              // Delegate to a custom event via window to avoid threading through props deeply
              const event = new CustomEvent('edit-payment-save', { detail: { resident: res, period: per, updates } });
              window.dispatchEvent(event);
              setEditingPeriodId(null);
              return true; // actual persistence handled by App listener
            }}
          />
        </div>
      ))}
      </div>
      
      <NotificationModal
        visible={notification.visible}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={() => setNotification({ ...notification, visible: false })}
      />
      
      {confirmation && (
        <ConfirmationModal
          visible={confirmation.visible}
          title={confirmation.title}
          message={confirmation.message}
          confirmText="Ya, Hapus"
          cancelText="Batal"
          type="danger"
          onConfirm={confirmation.onConfirm}
          onCancel={() => setConfirmation(null)}
        />
      )}
    </>
  );
}